import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

export function PasswordDialog({
  password: initialPassword,
  title,
  description,
  onClose,
  onApply,
  isApplying = false,
}: {
  password: string
  title?: string
  /** Подсказка под заголовком; если не задана — зависит от режима */
  description?: string
  onClose: () => void
  /** Если задан — поле ввода и кнопка «Применить» (сброс пароля) */
  onApply?: (password: string) => void | Promise<void>
  isApplying?: boolean
}) {
  const { t } = useTranslation()
  const [value, setValue] = useState(initialPassword)
  const [copied, setCopied] = useState(false)
  const isReset = Boolean(onApply)

  const hint =
    description ??
    (isReset
      ? t('password.dialog.resetHint')
      : t('password.dialog.createHint'))

  function handleClose() {
    if (!isApplying) onClose()
  }

  return (
    <Modal
      title={title ?? t('password.dialog.createdTitle')}
      onClose={handleClose}
      footer={
        isReset ? (
          <>
            <Button variant="outline" onClick={handleClose} disabled={isApplying}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={isApplying || value.trim() === ''}
              onClick={async () => {
                if (!onApply) return
                try {
                  await onApply(value.trim())
                } catch {
                  // Ошибка показывается через onError мутации / родителя
                }
              }}
            >
              {isApplying ? t('password.dialog.applying') : t('common.apply')}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleClose}>
            {t('password.dialog.close')}
          </Button>
        )
      }
    >
      <p className="mb-4 text-sm text-[var(--stryzh-color-text-secondary)]">{hint}</p>
      {isReset ? (
        <Input
          label={t('password.new')}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setCopied(false)
          }}
          autoComplete="new-password"
          className="font-[family-name:var(--stryzh-font-family-mono)]"
        />
      ) : (
        <div className="rounded-[var(--stryzh-radius-md)] bg-[color:var(--stryzh-color-fill-input)] p-4 font-[family-name:var(--stryzh-font-family-mono)] text-lg break-all">
          {initialPassword}
        </div>
      )}
      <Button
        className="mt-4"
        variant="secondary"
        onClick={async () => {
          const text = isReset ? value : initialPassword
          await navigator.clipboard.writeText(text)
          setCopied(true)
        }}
      >
        {copied ? t('password.dialog.copied') : t('password.dialog.copy')}
      </Button>
    </Modal>
  )
}
